import { Container, Singleton } from 'typescript-ioc'
import { AppService } from './app'
import { IApiResponse } from '@pushly/aqe/lib/interfaces/index'
import aqe from '@pushly/aqe'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '../config/axios-setup'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { FeedConfiguration } from '@pushly/models/lib/structs/feeds/feed-configuration'
import { FeedType } from '@pushly/models/lib/enums/feed-type'
import { FeedNotificationPreview } from '@pushly/models/lib/structs/feeds/feed-notification-preview'

interface IFeedPreviewApiResponse {
    notifications: FeedNotificationPreview[]
    estimated_delivery_times: string[]
}

@Singleton
export class FeedService {
    private appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async create(domainId: number, createDto: any, opts: IApiCallOptions = {}): Promise<IApiResponse<Feed>> {
        let ok = false
        let feed: Feed

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = Feed.from(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed! }
    }

    public async patchFeed(
        domainId: number,
        feedId: number,
        updateDto: MutableFeed | { enabled: boolean },
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<Feed>> {
        let ok = false
        let feed: Feed

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: 'serialize' in updateDto ? updateDto.serialize({ exposeUnsetFields: false }) : updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = Feed.from(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed! }
    }

    public async fetchFeedById(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<{ data: Feed; meta: any; ok: boolean }> {
        let ok = false
        let feed: Feed = new MutableFeed()
        let meta: any

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )

            feed = res.data.data
            ok = true
            meta = res.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }
        }
        return { ok, data: feed, meta }
    }

    public async fetchFeedPreview(
        domainId: number,
        feedConfig: FeedConfiguration,
        url: string,
        feedType: FeedType,
        numOfArticles: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IFeedPreviewApiResponse>> {
        let ok = false
        let data: IFeedPreviewApiResponse = {
            notifications: [],
            estimated_delivery_times: [],
        }
        let meta: any
        let cancelled = false

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/preview?numOfArticles=${numOfArticles}`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: {
                        previewConfig: {
                            domain_id: domainId,
                            feed_config: feedConfig.serialize(),
                            url,
                            feed_type: feedType,
                        },
                    },
                },
                opts.cancellationKey,
            )

            ok = true

            const notifData = req.data.data?.notifications.map((n) => FeedNotificationPreview.parseJsonObject(n))

            const estDeliveryTimeData = req.data.data?.estimatedDeliveryTimes
            data = {
                notifications: notifData,
                estimated_delivery_times: estDeliveryTimeData,
            }
            meta = req.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response?.data.message)
            }

            cancelled = isAxiosCancellation(error)
        }

        return { ok, data, meta, cancelled }
    }
}
